import React, { useState, useEffect } from "react";
import { Button, Container, Row, Col, Form, Card, ProgressBar } from "react-bootstrap";
import { FlagFill, Flag } from 'react-bootstrap-icons';
import CountdownClock from "./CountdownClock";
import './Question.css';

// Shuffle function to randomize the order of the options
const shuffleArray = (array) => {
    return array
        .map(value => ({ value, sort: Math.random() }))
        .sort((a, b) => a.sort - b.sort)
        .map(({ value }) => value);
};

const Question = ({
    question, onNext, onBack, onMark, totalQuestions, handleAnswer,
    markedQuestions, answers, currentQuestion, onGoTo, isReview, correctAnswers
}) => {
    const [selectedAnswer, setSelectedAnswer] = useState('');
    const [shuffledOptions, setShuffledOptions] = useState([]);
    const [localAnswers, setLocalAnswers] = useState(answers);
    const [timeLeft, setTimeLeft] = useState(90 * 60); // Setting initial time to 90 minutes

    useEffect(() => {
        if (question.options) {
            // Shuffle options only when the question changes
            setShuffledOptions(shuffleArray(question.options));
        }
    }, [question.options]);

    useEffect(() => {
        if (answers[question.newId]) {
            setSelectedAnswer(answers[question.newId]);
        } else {
            setSelectedAnswer('');
        }
        setLocalAnswers(answers);
    }, [question.newId, answers]);

    useEffect(() => {
        // Chỉ đếm thời gian khi không ở chế độ xem lại
        if (!isReview) {
            const timer = setInterval(() => {
                setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
            }, 1000);
            return () => clearInterval(timer);
        }
        // Khi ở chế độ xem lại, đồng hồ vẫn hiển thị nhưng không đếm thời gian
    }, [isReview]);

    const handleSelectAnswer = (e) => {
        if (!isReview) {
            const newAnswer = e.target.value;
            setSelectedAnswer(newAnswer);
            handleAnswer(question.newId, newAnswer);
            setLocalAnswers(prev => ({ ...prev, [question.newId]: newAnswer }));
        }
    };

    const renderQuestionGrid = () => {
        return (
            <div className="question-grid">
                {Array.from({ length: totalQuestions }).map((_, index) => {
                    const questionId = index + 1;
                    const isAnswered = localAnswers.hasOwnProperty(questionId);
                    const isMarked = markedQuestions.includes(questionId);
                    const isCurrent = currentQuestion === questionId;

                    // Check if this question has a correct/incorrect status in review mode
                    const isCorrect = isReview && correctAnswers && correctAnswers[questionId] === true;
                    const isIncorrect = isReview && correctAnswers && correctAnswers[questionId] === false;

                    let buttonClass = 'question-button ';
                    
                    // Apply appropriate class based on review status
                    if (isReview) {
                        if (isCorrect) {
                            buttonClass += 'correct ';
                        } else if (isIncorrect) {
                            buttonClass += 'incorrect ';
                        } else {
                            buttonClass += isAnswered ? 'answered-not-current ' : 'not-answered ';
                        }
                    } else {
                        // Normal mode styling
                        buttonClass += isAnswered ? (isCurrent ? 'answered ' : 'answered-not-current ') : 'not-answered ';
                    }
                    
                    if (isCurrent) {
                        buttonClass += 'current';
                    }

                    return (
                        <div
                            key={questionId}
                            className={buttonClass}
                            onClick={() => onGoTo(questionId)}
                        >
                            {isMarked ? <FlagFill className="flag-icon" /> : null}
                            {questionId}
                        </div>
                    );
                })}
            </div>
        );
    };

    // Find the correct option for the current question
    // const getCorrectOption = () => {
    //     if (!question.options) return null;
    //     return question.options.find(option => option.isCorrect);
    // };
    
    //const correctOption = getCorrectOption();

    return (
        <Container className="question-component-wrapper mt-4">
            <Row className="mb-3">
                <Col lg={6} md={12} className="mb-4">
                    <Card className="question-card shadow p-2 mb-3 w-100">
                        <Card.Body>
                            <Card.Title>
                                {markedQuestions.includes(currentQuestion) && (
                                    <FlagFill color="red" className="mr-5" />
                                )}
                                Câu {currentQuestion}
                            </Card.Title>
                            <Card.Text>{question.questionText}</Card.Text>

                            <Form.Group as={Row} className="mb-3 my-3">
                                <Col sm="12">
                                    {shuffledOptions.map((option, index) => {
                                        const isCorrect = option.isCorrect;
                                        const isSelected = selectedAnswer === option.text;

                                        // Styling for review mode
                                        let optionClassName = "option-item";
                                        let labelStyle = {};

                                        if (isReview) {
                                            if (isCorrect) {
                                                // Always highlight correct answers in green in review mode
                                                labelStyle = { color: 'green', fontWeight: 'bold' };
                                                optionClassName += " correct-answer";
                                            } else if (isSelected && !isCorrect) {
                                                // Highlight incorrect selected answers in red
                                                labelStyle = { color: 'red', fontWeight: 'bold' };
                                                optionClassName += " incorrect-answer";
                                            }
                                        }

                                        return (
                                            <Form.Check
                                                key={index}
                                                type="radio"
                                                id={`option-${question.newId}-${index}`}
                                                label={option.text}
                                                value={option.text}
                                                checked={isSelected}
                                                onChange={handleSelectAnswer}
                                                disabled={isReview}
                                                style={labelStyle}
                                                className={`mx-2 ${optionClassName}`}
                                                // Thêm sự kiện click vào toàn bộ Form.Check
                                                onClick={(e) => {
                                                    // Đảm bảo không gọi hàm nếu đang ở chế độ review
                                                    if (!isReview) {
                                                        // Nếu click vào form-check, nhưng không phải vào input radio
                                                        if (e.target.type !== 'radio') {
                                                            // Tránh xử lý kép nếu click vào radio hoặc label (vì onChange đã xử lý)
                                                            if (!e.target.classList.contains('form-check-label') && 
                                                                !e.target.classList.contains('form-check-input')) {
                                                                // Xử lý click vào vùng khác trong form-check
                                                                handleSelectAnswer({ target: { value: option.text } });
                                                            }
                                                        }
                                                    }
                                                }}
                                            />
                                        );
                                    })}
                                </Col>
                            </Form.Group>
                            
                            {/* Phần giải thích đã được loại bỏ theo yêu cầu */}

                            <Row className="my-3 d-flex justify-content-between">
                                <Col>
                                    <Button variant="secondary" onClick={onBack} disabled={currentQuestion === 1}>
                                        Back
                                    </Button>
                                </Col>
                                <Col className="d-flex justify-content-center">
                                    <div
                                        onClick={() => !isReview && onMark(currentQuestion)}
                                        style={{ cursor: isReview ? 'default' : 'pointer' }}
                                    >
                                        {markedQuestions.includes(currentQuestion) ? (
                                            <FlagFill color="red" size={30} />
                                        ) : (
                                            <Flag size={30} />
                                        )}
                                    </div>
                                </Col>
                                <Col className="d-flex justify-content-end">
                                    <Button variant="primary" onClick={onNext} disabled={currentQuestion === totalQuestions}>
                                        Next
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={6} md={12}>
                    <div className="py-3">
                        <CountdownClock timeLeft={timeLeft} />
                        <ProgressBar variant="info" now={(timeLeft / (90 * 60)) * 100} className="time-progress-bar" />
                        {isReview && (
                            <div className="review-status-banner mt-3">
                                <h3 className="review-title">Kết quả bài làm</h3>
                            </div>
                        )}
                    </div>
                    <div className="my-2">
                        {renderQuestionGrid()}
                    </div>
                </Col>
            </Row>
        </Container>
    );
};

export default Question;